import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ChevronDown, ChevronUp, X, Filter, Save, AlertCircle } from 'lucide-react';
import SearchableFilter from './SearchableFilter';
import LocationFilter from './LocationFilter';
import InfoTooltip from './InfoTooltip';
import apiClient from '../utils/apiClient';
// Utility function to capitalize words
const capitalizeWords = (str) => {
  if (!str || typeof str !== 'string') return str;
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};
// Constants
const API_BASE = process.env.REACT_APP_API_URL||'http://localhost:8000';
const DEFAULT_FILTERS = { weights: [], types: [], brands: [], strains: [], dispensaries: [], states: [] };
const DEFAULT_LOCATION = { zipcode: '', radius: 25 };

// Tab-specific descriptions
const getFilterDescriptions = (activeTab) => {
  const isFlower = activeTab === 'flower';
  
  return {
    weight: isFlower 
      ? "Filter flower products by their weight or size. Select one or more weights to narrow down your search. Common weights are eighths (3.5g), quarter ounces (7g), half ounces (14g), and ounces (28g)."
      : "Filter concentrates by their weight or size. Select one or more weights to narrow down your search. Common weights for concentrates are 0.5g, 1g, 2g, and 4g.",
    type: isFlower
      ? "Filter by flower product types. Choose from different flower categories like sativa, indica, hybrid, and 1:1 or high CBD."
      : "Filter by concentrate types. Choose from various concentrate categories like shatter, wax, live resin, rosin, distillate, hash, and other extraction methods."
  };
};

// Super simple, fast checkbox component
const FastCheckbox = ({ value, label, checked, onChange }) => (
  <label className="flex items-center space-x-2 cursor-pointer py-1">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(value, e.target.checked)}
      className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
    />
    <span className="text-sm text-gray-700">{label}</span>
  </label>
);


// Simple filter section - no fancy search, just checkboxes
const SimpleFilterSection = ({ title, options, selected, onChange, description }) => {
  // Only show first 50 items by default, with "show more" button
  const [showAll, setShowAll] = useState(false);
  const displayOptions = showAll ? options : options.slice(0, 50);
  
  return (
    <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-200">
      <div className="flex items-center gap-2 mb-2">
        <h3 className="font-semibold text-gray-900 text-sm">{title}</h3>
        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
          {options.length} available
        </span>
        {description && <InfoTooltip description={description} />}
      </div>
      <div className="max-h-48 overflow-y-scroll space-y-1" style={{scrollbarWidth: 'thin'}}>
        {displayOptions.map((option) => (
          <FastCheckbox
            key={option}
            value={option}
            label={option}
            checked={selected.includes(option)}
            onChange={onChange}
          />
        ))}
        {!showAll && options.length > 50 && (
          <button
            onClick={() => setShowAll(true)}
            className="text-xs text-green-600 hover:text-green-700 mt-2"
          >
            Show {options.length - 50} more...
          </button>
        )}
      </div>
    </div>
  );
};

// Filter tag for showing active filters
const FilterTag = ({ label, onRemove }) => (
  <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
    {label}
    <button onClick={onRemove} className="ml-1 text-green-600 hover:text-green-800">
      <X size={12} />
    </button>
  </span>
);

// Confirmation Modal Component
const SavePreferencesModal = ({ isOpen, onClose, onConfirm }) => {
  // Handle ESC key press
  useEffect(() => {
    if (!isOpen) return;
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center space-x-3 mb-4">
          <AlertCircle className="w-6 h-6 text-orange-500" />
          <h3 className="text-lg font-semibold text-gray-900">Save as Default Filters?</h3>
        </div>
        <p className="text-gray-600 mb-6">
          Are you sure you want to save your current filter selections as your default? 
          These will be automatically applied when you visit the leaderboard page.
        </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
          >
            Save as Default
          </button>
        </div>
      </div>
    </div>
  );
};

// Main fast filter panel
const FastFilterPanel = ({ activeTab, appliedFilters, appliedLocation, appliedSortBy, onApplyFilters, onApplyLocation, isExpanded, onToggleExpanded, isAuthenticated }) => {
  const [draftFilters, setDraftFilters] = useState(appliedFilters);
  const [draftLocation, setDraftLocation] = useState(appliedLocation || DEFAULT_LOCATION);
  const [filterOptions, setFilterOptions] = useState({
    weights: [],
    brands: [],
    types: [],
    strains: [],
    dispensaries: [],
    states: []
  });
  const [loading, setLoading] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [savingPreferences, setSavingPreferences] = useState(false);
  
  // Update draft when applied filters change
  useEffect(() => {
    setDraftFilters(appliedFilters);
  }, [appliedFilters]);
  
  // Update draft when applied location changes
  useEffect(() => {
    setDraftLocation(appliedLocation || DEFAULT_LOCATION);
  }, [appliedLocation]);
  
  // Load filter options - CASCADING FILTERS
  useEffect(() => {
    if (!isExpanded) return; // Only load when expanded
    
    let isCancelled = false;
    setLoading(true);
    
    const loadFilters = async () => {
      try {
        // Build query parameters for cascading filters
        const params = new URLSearchParams();
        
        // Use APPLIED filters for cascading (not draft filters)
        // This ensures filter options only change after Apply is clicked
        if (appliedFilters.weights.length > 0) {
          params.append('weights', appliedFilters.weights.join(','));
        }
        if (appliedFilters.types.length > 0) {
          params.append('types', appliedFilters.types.join(','));
        }
        if (appliedFilters.brands.length > 0) {
          params.append('brands', appliedFilters.brands.join(','));
        }
        if (appliedFilters.strains.length > 0) {
          params.append('strains', appliedFilters.strains.join(','));
        }
        if (appliedFilters.dispensaries.length > 0) {
          params.append('dispensaries', appliedFilters.dispensaries.join(','));
        }
        if (appliedFilters.states.length > 0) {
          params.append('states', appliedFilters.states.join(','));
        }
        
        // Add location filters
        if (appliedLocation && appliedLocation.zipcode) {
          params.append('zipcode', appliedLocation.zipcode);
          params.append('radius', appliedLocation.radius.toString());
        }
        
        // Use the new cascading filter endpoint
        const url = `${API_BASE}/filters/filtered/${activeTab}?${params.toString()}`;
        const response = await fetch(url);
        
        if (!isCancelled) {
          if (response.ok) {
            const result = await response.json();
            if (result.success && result.data) {
              setFilterOptions({
                weights: result.data.weights || [],
                brands: (result.data.brands || []).map(capitalizeWords),
                types: result.data.types || [],
                strains: result.data.strains || [],
                dispensaries: result.data.dispensaries || [],
                states: result.data.states || []
              });
            }
          } else {
            // Fallback to the original endpoint if the new one fails
            const fallbackResponse = await fetch(`${API_BASE}/filters/all/${activeTab}`);
            if (fallbackResponse.ok) {
              const fallbackResult = await fallbackResponse.json();
              if (fallbackResult.success && fallbackResult.data) {
                setFilterOptions({
                  weights: fallbackResult.data.weights || [],
                  brands: (fallbackResult.data.brands || []).map(capitalizeWords),
                  types: fallbackResult.data.types || [],
                  strains: fallbackResult.data.strains || [],
                  dispensaries: fallbackResult.data.dispensaries || [],
                  states: fallbackResult.data.states || []
                });
              }
            }
          }
          setLoading(false);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('Filter loading error:', error);
          // Try fallback endpoint on error
          try {
            const fallbackResponse = await fetch(`${API_BASE}/filters/all/${activeTab}`);
            if (fallbackResponse.ok) {
              const fallbackResult = await fallbackResponse.json();
              if (fallbackResult.success && fallbackResult.data) {
                setFilterOptions({
                  weights: fallbackResult.data.weights || [],
                  brands: (fallbackResult.data.brands || []).map(capitalizeWords),
                  types: fallbackResult.data.types || [],
                  strains: fallbackResult.data.strains || [],
                  dispensaries: fallbackResult.data.dispensaries || [],
                  states: fallbackResult.data.states || []
                });
              }
            }
          } catch (fallbackError) {
            console.error('Fallback filter loading error:', fallbackError);
          }
          setLoading(false);
        }
      }
    };
    
    // Small delay to prevent blocking UI
    const timer = setTimeout(loadFilters, 10);
    
    return () => {
      isCancelled = true;
      clearTimeout(timer);
    };
  }, [activeTab, isExpanded, appliedFilters, appliedLocation]);
  
  // Get tab-specific descriptions
  const filterDescriptions = useMemo(() => getFilterDescriptions(activeTab), [activeTab]);

  // Count active filters
  const activeCount = useMemo(() => {
    const filterCount = appliedFilters.weights.length + appliedFilters.types.length + 
                       appliedFilters.brands.length + appliedFilters.strains.length + 
                       appliedFilters.dispensaries.length + appliedFilters.states.length;
    const locationCount = appliedLocation && appliedLocation.zipcode ? 1 : 0;
    return filterCount + locationCount;
  }, [appliedFilters, appliedLocation]);
  
  // Check if there are pending changes
  const hasPendingChanges = useMemo(() => {
    const filtersChanged = JSON.stringify(draftFilters) !== JSON.stringify(appliedFilters);
    const locationChanged = JSON.stringify(draftLocation) !== JSON.stringify(appliedLocation || DEFAULT_LOCATION);
    return filtersChanged || locationChanged;
  }, [draftFilters, appliedFilters, draftLocation, appliedLocation]);
  
  // Simple handlers
  const handleWeightChange = useCallback((value, checked) => {
    setDraftFilters(prev => ({
      ...prev,
      weights: checked 
        ? [...prev.weights, value]
        : prev.weights.filter(w => w !== value)
    }));
  }, []);
  
  const handleTypeChange = useCallback((value, checked) => {
    setDraftFilters(prev => ({
      ...prev,
      types: checked 
        ? [...prev.types, value]
        : prev.types.filter(t => t !== value)
    }));
  }, []);
  
  const handleBrandChange = useCallback((value, checked) => {
    setDraftFilters(prev => ({
      ...prev,
      brands: checked 
        ? [...prev.brands, value]
        : prev.brands.filter(b => b !== value)
    }));
  }, []);
  
  const handleStrainChange = useCallback((value, checked) => {
    setDraftFilters(prev => ({
      ...prev,
      strains: checked 
        ? [...prev.strains, value]
        : prev.strains.filter(s => s !== value)
    }));
  }, []);
  
  const handleDispensaryChange = useCallback((value, checked) => {
    setDraftFilters(prev => ({
      ...prev,
      dispensaries: checked 
        ? [...prev.dispensaries, value]
        : prev.dispensaries.filter(d => d !== value)
    }));
  }, []);
  
  const handleStateChange = useCallback((value, checked) => {
    setDraftFilters(prev => ({
      ...prev,
      states: checked 
        ? [...prev.states, value]
        : prev.states.filter(s => s !== value)
    }));
  }, []);
  
  // Location handlers
  const handleZipcodeChange = useCallback((value) => {
    setDraftLocation(prev => ({
      ...prev,
      zipcode: value
    }));
  }, []);
  
  const handleRadiusChange = useCallback((value) => {
    setDraftLocation(prev => ({
      ...prev,
      radius: value
    }));
  }, []);
  
  const handleLocationClear = useCallback(() => {
    setDraftLocation(DEFAULT_LOCATION);
  }, []);
  
  const handleApply = useCallback(() => {
    onApplyFilters(draftFilters);
    onApplyLocation(draftLocation);
  }, [draftFilters, draftLocation, onApplyFilters, onApplyLocation]);
  
  const handleReset = useCallback(() => {
    setDraftFilters(appliedFilters);
    setDraftLocation(appliedLocation || DEFAULT_LOCATION);
  }, [appliedFilters, appliedLocation]);
  
  const handleClearAll = useCallback(() => {
    setDraftFilters(DEFAULT_FILTERS);
    setDraftLocation(DEFAULT_LOCATION);
    // Apply the cleared filters immediately to refresh the options
    onApplyFilters(DEFAULT_FILTERS);
    onApplyLocation(DEFAULT_LOCATION);
  }, [onApplyFilters, onApplyLocation]);
  
  const removeFilter = useCallback((type, value) => {
    const newFilters = { ...appliedFilters };
    newFilters[type] = newFilters[type].filter(item => item !== value);
    onApplyFilters(newFilters);
    setDraftFilters(newFilters);
  }, [appliedFilters, onApplyFilters]);

  const handleSaveAsDefault = useCallback(async () => {
    setSavingPreferences(true);
    try {
      await apiClient.post('/user/preferences', {
        product_type: activeTab,  // Add this line - pass the current tab as product_type
        weights: appliedFilters.weights,
        types: appliedFilters.types,
        brands: appliedFilters.brands,
        strains: appliedFilters.strains,
        dispensaries: appliedFilters.dispensaries,
        states: appliedFilters.states,
        zipcode: appliedLocation?.zipcode || null,
        radius: appliedLocation?.radius || null,
        sort_by: appliedSortBy || 'cost_asc'
      });
      
      setShowSaveModal(false);
      // Optionally show a success message
      alert('Default filters saved successfully!');
    } catch (error) {
      console.error('Failed to save preferences:', error);
      alert('Failed to save default filters. Please try again.');
    } finally {
      setSavingPreferences(false);
    }
  }, [appliedFilters, appliedLocation, appliedSortBy, activeTab]); // Add activeTab to dependencies

  return (
    <div className="mb-6">
      {/* Header */}
      <button
        onClick={onToggleExpanded}
        className="flex items-center justify-between w-full p-3 bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
      >
        <div className="flex items-center space-x-3">
          <Filter size={18} className="text-green-600" />
          <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
          {activeCount > 0 && (
            <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
              {activeCount}
            </span>
          )}
          {hasPendingChanges && (
            <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs">
              Changes pending
            </span>
          )}
        </div>
        {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      
      {isExpanded && (
        <div className="mt-3 space-y-4">
          {/* Active filters */}
          {activeCount > 0 && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Active Filters:</h3>
              <div className="flex flex-wrap gap-1">
                {appliedFilters.weights.map(weight => (
                  <FilterTag
                    key={`weight-${weight}`}
                    label={`Weight: ${weight}`}
                    onRemove={() => removeFilter('weights', weight)}
                  />
                ))}
                {appliedFilters.types.map(type => (
                  <FilterTag
                    key={`type-${type}`}
                    label={`Type: ${type}`}
                    onRemove={() => removeFilter('types', type)}
                  />
                ))}
                {appliedFilters.brands.map(brand => (
                  <FilterTag
                    key={`brand-${brand}`}
                    label={`Brand: ${brand}`}
                    onRemove={() => removeFilter('brands', brand)}
                  />
                ))}
                {appliedFilters.strains.map(strain => (
                  <FilterTag
                    key={`strain-${strain}`}
                    label={`Strain: ${strain}`}
                    onRemove={() => removeFilter('strains', strain)}
                  />
                ))}
                {appliedFilters.dispensaries.map(dispensary => (
                  <FilterTag
                    key={`dispensary-${dispensary}`}
                    label={`Dispensary: ${dispensary}`}
                    onRemove={() => removeFilter('dispensaries', dispensary)}
                  />
                ))}
              </div>
            </div>
          )}
          
          {/* Location Filter - Moved to top for customer priority */}
          <div className="mb-4">
            <LocationFilter
              zipcode={draftLocation.zipcode}
              radius={draftLocation.radius}
              onZipcodeChange={handleZipcodeChange}
              onRadiusChange={handleRadiusChange}
              onClear={handleLocationClear}
              states={draftFilters.states}
              onStatesChange={handleStateChange}
              availableStates={filterOptions.states}
              disabled={loading}
            />
          </div>
          
          {/* Filter options */}
          {loading ? (
            <div className="text-center py-4">
              <div className="text-sm text-gray-500">Loading filtered options...</div>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Show filter context info */}
              {activeCount > 0 && (
                <div className="p-2 bg-blue-50 rounded-md border border-blue-200">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span className="text-xs text-blue-700">
                      Showing options available with your current filters
                    </span>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <SearchableFilter
                title="Brand"
                options={filterOptions.brands}
                selected={draftFilters.brands}
                onChange={handleBrandChange}
                description="Filter by cannabis brand, farm, or manufacturer. Use the search box to quickly find your favorite farms. You can select multiple brands to compare their products."
              />
              <SearchableFilter
                title="Strain"
                options={filterOptions.strains}
                selected={draftFilters.strains}
                onChange={handleStrainChange}
                description="Filter by cannabis strain name. Search for your favorite strains, or search for partial names like 'haze' or 'cookies' to see all strains that match. You can select multiple strains to see their availability across dispensaries."
              />
              <SearchableFilter
                title="Dispensary"
                options={filterOptions.dispensaries}
                selected={draftFilters.dispensaries}
                onChange={handleDispensaryChange}
                description="Filter by dispensary location. Use the search to find specific dispensaries in your area. You can also search by town name. Type the name of a town or city to see all participating dispensaries in that area. Select multiple dispensaries to compare prices and availability."
              />
              </div>
            </div>
          )}
          
          {/* Weight and Type filters - Moved to bottom */}
          {!loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
              <SimpleFilterSection
                title="Weight"
                options={filterOptions.weights}
                selected={draftFilters.weights}
                onChange={handleWeightChange}
                description={filterDescriptions.weight}
              />
              <SimpleFilterSection
                title="Type"
                options={filterOptions.types}
                selected={draftFilters.types}
                onChange={handleTypeChange}
                description={filterDescriptions.type}
              />
            </div>
          )}
          
          {/* Action buttons */}
          <div className="flex justify-between items-center pt-3 border-t">
            <div className="space-x-2">
              <button
                onClick={handleClearAll}
                className="px-3 py-2 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Clear All
              </button>
              {hasPendingChanges && (
                <button
                  onClick={handleReset}
                  className="px-3 py-2 text-sm bg-orange-100 text-orange-700 rounded hover:bg-orange-200"
                >
                  Reset
                </button>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              {isAuthenticated && (
                <button
                  onClick={() => setShowSaveModal(true)}
                  disabled={savingPreferences}
                  className="flex items-center space-x-2 px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Save current filters as default"
                >
                  <Save size={16} />
                  <span>Save as Default</span>
                </button>
              )}
              <button
                onClick={handleApply}
                disabled={!hasPendingChanges}
                className={`px-4 py-2 rounded font-medium ${
                  hasPendingChanges
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Save Preferences Confirmation Modal */}
      <SavePreferencesModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onConfirm={handleSaveAsDefault}
      />
    </div>
  );
};

export default FastFilterPanel;
